import { z } from "zod";
import { AccountZ, AccountRoleZ, AccountPhaseZ } from "../domain/account";
import { PlatformTypeZ, AdapterTypeZ } from "../domain/platform";

/**
 * Forward-declared here (real def lives below) so CreateAccountReq can
 * accept the wizard's pre-picked challenge type without pulling in the
 * whole rules schema.
 */
const ChallengeTypeForCreateZ = z.enum([
  "instant",
  "1-step",
  "2-step",
  "3-step",
  "evaluation",
  "other",
]);

export const CreateAccountReqZ = z.object({
  id: z.string().min(1).max(32),
  firmId: z.string().min(1).max(32),
  platformType: PlatformTypeZ,
  adapter: AdapterTypeZ,
  role: AccountRoleZ,
  phase: AccountPhaseZ,
  challengeFee: z.number().nullable().optional(),
  login: z.string().nullable().optional(),
  /** Plaintext password — stored encrypted-at-rest in D1 (Cloudflare's
   *  storage encryption). Required for playwright adapter accounts so
   *  the validation worker can log in. Optional for the legacy MT5 EA
   *  adapter (EA carries its own credentials). */
  password: z.string().nullable().optional(),
  brokerServer: z.string().nullable().optional(),
  /** TradeLocker requires a per-account login URL (the broker's terminal
   *  domain). MT5 / Match-Trader resolve this from firms.terminal_url. */
  loginUrl: z.string().url().nullable().optional(),
  /** Match-Trader dashboards host every account the user has (challenge,
   *  funded, retired) behind one email+password. The label is whatever
   *  identifies *this* specific account in the dashboard — usually the
   *  account number or the label under the account card. Required when
   *  `platformType === "match-trader"`; ignored elsewhere. */
  accountLabel: z.string().nullable().optional(),
  /** Wizard-picked variant. All optional: the old code path (no program
   *  step, or firms with zero firm_challenges data) still works — these
   *  fields stay NULL in the row and get filled during the rules step. */
  accountSize: z.number().int().positive().nullable().optional(),
  steps: z.number().int().min(0).max(5).nullable().optional(),
  challengeType: ChallengeTypeForCreateZ.nullable().optional(),
  /** Program label (e.g. "Rapid", "Swing", "Standard"). Not persisted on
   *  the account row today — carried through for downstream wiring
   *  when/if we add a program_name column. */
  programName: z.string().max(64).nullable().optional(),
  /** Stable reference to the firm_challenges row the operator picked in
   *  the wizard's Program step. Format: the numeric `id` column cast to
   *  string. Lets /api/accounts/:id/rules pull the pre-filled rule set
   *  directly from firm_challenges instead of re-inferring from (size,
   *  steps) — which breaks when the same firm has multiple programs at
   *  the same size/step count. Null for manual-entry accounts. */
  firmChallengeRef: z.string().max(32).nullable().optional(),
  /** Unix ms when the operator ticked "Yes, this is my terminal" in the
   *  wizard's Credentials step. Distinct from the implicit bot-success
   *  verification; this stamps that the OPERATOR confirmed the URL +
   *  MT5 server are correct before the bot ever tried them. */
  terminalVerifiedAt: z.number().int().nullable().optional(),
});
export type CreateAccountReq = z.infer<typeof CreateAccountReqZ>;

/**
 * Every per-account rule the AI needs to make a compliant decision.
 * Every field is optional — the dashboard pre-fills from detection
 * when it can and falls back to blank inputs when the firm's record
 * is thin (new / niche firms).
 *
 * Bounds exist where a typo ("50" instead of "5") could quietly ask
 * Claude to let the account lose half its balance.
 */
export const ChallengeTypeZ = z.enum([
  "instant",
  "1-step",
  "2-step",
  "3-step",
  "evaluation",
  "other",
]);
export type ChallengeType = z.infer<typeof ChallengeTypeZ>;

export const DailyDdTypeZ = z.enum(["eod", "static", "intraday_trailing"]);
export type DailyDdType = z.infer<typeof DailyDdTypeZ>;

export const MaxDdTypeZ = z.enum(["static", "trailing", "trailing_from_initial"]);
export type MaxDdType = z.infer<typeof MaxDdTypeZ>;

export const AccountRulesZ = z.object({
  accountSize: z.number().int().positive().nullable().optional(),
  steps: z.number().int().min(0).max(5).nullable().optional(),
  challengeType: ChallengeTypeZ.nullable().optional(),
  dailyLossPct: z.number().min(0).max(25).nullable().optional(),
  dailyDdType: DailyDdTypeZ.nullable().optional(),
  maxDrawdownPct: z.number().min(0).max(50).nullable().optional(),
  maxDdType: MaxDdTypeZ.nullable().optional(),
  profitTargetPct: z.number().min(0).max(100).nullable().optional(),
  profitTargetStep1Pct: z.number().min(0).max(100).nullable().optional(),
  profitTargetStep2Pct: z.number().min(0).max(100).nullable().optional(),
  profitTargetStep3Pct: z.number().min(0).max(100).nullable().optional(),
  profitSplitPct: z.number().min(0).max(100).nullable().optional(),
  // Prop firms publish free-text descriptions that routinely exceed the
  // old 32/16 ceilings — e.g. "On-demand from day 5 · weekly after" or
  // "1:100 FX / 1:20 Crypto / 1:10 Commodities". Generous limits here;
  // the DB column is TEXT so there's no storage cost, and the AI prompt
  // truncates anyway.
  payoutFrequency: z.string().max(128).nullable().optional(),
  leverage: z.string().max(64).nullable().optional(),
  leverageFx: z.string().max(64).nullable().optional(),
  leverageIndices: z.string().max(64).nullable().optional(),
  leverageMetals: z.string().max(64).nullable().optional(),
  leverageCommodities: z.string().max(64).nullable().optional(),
  leverageCrypto: z.string().max(64).nullable().optional(),
  minTradingDays: z.number().int().min(0).max(60).nullable().optional(),
  weekendHoldingAllowed: z.boolean().nullable().optional(),
  newsTradingAllowed: z.boolean().nullable().optional(),
  consistencyRulePct: z.number().min(0).max(100).nullable().optional(),
  /** Cap on % of starting balance at risk per single trade. Most firms
   *  only enforce on instant-funded; null means no per-trade cap. */
  maxRiskPerTradePct: z.number().min(0).max(50).nullable().optional(),
  /** Absolute lot cap per ticket. Firm-specific; null means no cap. */
  maxLotSize: z.number().min(0).max(1000).nullable().optional(),
  /** Min profit % required before first payout request is allowed.
   *  Instant-specific. Calculated against starting balance. */
  payoutThresholdPct: z.number().min(0).max(50).nullable().optional(),
  /** Profitable-day count required before payout (e.g. 3 of 5 must
   *  be green). Distinct from minTradingDays which counts ALL days. */
  minProfitableDays: z.number().int().min(0).max(60).nullable().optional(),
});
export type AccountRules = z.infer<typeof AccountRulesZ>;

/**
 * PATCH /api/accounts/:id/rules body. `confirm: true` stamps
 * `rules_confirmed_at` and kicks off auto-pair matching.
 */
export const PatchAccountRulesReqZ = AccountRulesZ.extend({
  confirm: z.boolean().optional(),
});
export type PatchAccountRulesReq = z.infer<typeof PatchAccountRulesReqZ>;

/**
 * GET /api/accounts/:id/rules response. `account` is the acknowledged
 * rule set (user-confirmed, may override detected values). `detected`
 * is what we found in firm_challenges / firm_intel — used by the
 * wizard to pre-fill and by the detail page to show "compared to
 * firm default" deltas. `variants` are matching firm_challenges rows
 * (one per challenge_type at the inferred account size).
 */
export const AccountRulesResZ = z.object({
  account: AccountRulesZ.extend({
    rulesConfirmedAt: z.number().nullable(),
    rulesEditedAt: z.number().nullable(),
  }),
  /** `detected` may carry an extra `rulesSource` field when the row was
   *  resolved via `firm_challenge_ref` — 'official_chunks' (synthesized
   *  from the firm's own pages), 'pfm' (propfirmmatch fallback), or
   *  'operator' (manual edit). The Rules step uses it to show an amber
   *  "partial coverage" chip when the pre-fill didn't come from the
   *  firm's authoritative pages. */
  detected: AccountRulesZ.extend({
    rulesSource: z.string().nullable().optional(),
  }).nullable(),
  inferredSize: z.number().int().nullable(),
  variants: z.array(z.record(z.unknown())),
  firmRulesRaw: z.string().nullable(),
});
export type AccountRulesRes = z.infer<typeof AccountRulesResZ>;

export const ValidateAccountResZ = z.object({
  ok: z.literal(true),
  accountId: z.string(),
  status: z.literal("validating"),
});
export type ValidateAccountRes = z.infer<typeof ValidateAccountResZ>;

export const ListAccountsResZ = z.object({
  accounts: z.array(AccountZ),
});
export type ListAccountsRes = z.infer<typeof ListAccountsResZ>;
